import React from 'react';
import { Link } from 'react-router-dom';
import type { MedicalDocument } from '../../api/documents';
import type { ClassifierConfig } from '../../api/admin';
import {
  formatClassifierValue,
  formatClassificationTag,
  formatOcrStatusShort,
  displayFileType,
} from '../../utils/documentLabels';

export type ReprocessAction = 'rerun-all' | 'rerun-ocr' | 'regenerate-summary' | 'reclassify' | null;

const statusLabel: Record<string, string> = {
  queued: 'In coada de procesare',
  processing_started: 'Procesare initiata',
  loading_images: 'Se incarca imaginile',
  ocr_extraction: 'Extractie OCR',
  processing: 'Procesare avansata',
  completed: 'Finalizat',
  error: 'Eroare',
  failed: 'Eroare procesare',
  summary_generating: 'Se genereaza rezumatul AI',
  summary_completed: 'Rezumat generat',
  summary_error: 'Eroare la generarea rezumatului',
  classification_generating: 'Se clasifica documentul',
  classification_completed: 'Document clasificat',
  classification_error: 'Eroare la clasificare',
};

function memberDocPipelineStage(doc: MedicalDocument): string {
  return doc.pipeline_status || doc.status || doc.ocr_processing_status || '';
}

// ----- File-type-aware icon + colour tile ---------------------------------
// Icons are rendered into a fixed-size "tile" (.doc-card-fileicon). Each tile
// gets a colour modifier matching the file type so a quick glance tells you
// what you're dealing with (PDF report vs. scanned image vs. Word file).
const fileIcon = (type: string) => {
  if (type === 'pdf' || type === 'doc' || type === 'docx' || type === 'txt' || type === 'rtf') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
      </svg>
    );
  }
  if (type === 'jpg' || type === 'jpeg' || type === 'png') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
};

const fileIconVariant = (type: string): string => {
  if (type === 'pdf') return 'doc-card-fileicon--pdf';
  if (type === 'jpg' || type === 'jpeg' || type === 'png') return 'doc-card-fileicon--img';
  if (type === 'doc' || type === 'docx') return 'doc-card-fileicon--doc';
  if (type === 'txt' || type === 'rtf') return 'doc-card-fileicon--txt';
  return 'doc-card-fileicon--default';
};

// Tone used by the top accent strip + status pill.
//   busy → animated amber
//   error → red
//   ready (ocr done / completed) → teal
//   anything else (e.g. brand new doc with no status yet) → muted slate
const cardTone = (
  doc: MedicalDocument,
  busy: boolean,
  ocrDone: boolean,
): 'busy' | 'error' | 'ready' | 'idle' => {
  const stage = memberDocPipelineStage(doc);
  if (stage === 'error' || stage === 'failed' || doc.classification_status === 'error' || doc.summary_status === 'error') {
    return 'error';
  }
  if (busy) return 'busy';
  if (ocrDone) return 'ready';
  return 'idle';
};

const isDocumentPipelineBusy = (doc: MedicalDocument) => {
  const s = doc.pipeline_status || doc.status || doc.ocr_processing_status || '';
  return [
    'queued',
    'processing_started',
    'loading_images',
    'ocr_extraction',
    'processing',
    'summary_generating',
    'classification_generating',
  ].includes(s);
};

// Narrow "OCR stage actively running" for THIS document. Used by the OCR
// icon spinner — must NOT include summary/classification statuses,
// otherwise clicking Reclasifică on a card would light the OCR icon too.
const isDocOcrRunning = (doc: MedicalDocument) => {
  const s = doc.pipeline_status || doc.status || doc.ocr_processing_status || '';
  return [
    'queued',
    'processing_started',
    'loading_images',
    'ocr_extraction',
    'processing',
  ].includes(s);
};

const isDocSummaryGenerating = (doc: MedicalDocument) =>
  doc.summary_status === 'generating' || doc.pipeline_status === 'summary_generating';

const isDocClassifying = (doc: MedicalDocument) =>
  doc.classification_status === 'generating' || doc.pipeline_status === 'classification_generating';

const isDocOcrDone = (doc: MedicalDocument) => {
  if (doc.ocr_processing_status === 'completed') return true;
  const s = doc.pipeline_status || '';
  if ([
    'completed',
    'summary_generating',
    'summary_completed',
    'summary_error',
    'classification_generating',
    'classification_completed',
    'classification_error',
  ].includes(s)) return true;
  return Boolean(doc.summary_status || doc.classification_status);
};

interface MemberDocumentCardProps {
  doc: MedicalDocument;
  isAdvanced: boolean;
  classifiers: ClassifierConfig[];
  /** Map of doc id → in-flight reprocess action. Drives spinner placement and
   *  cross-card disabling. Mirrors the same shape used inside MemberDetail. */
  docActiveActions: Record<number, ReprocessAction>;
  onRerunFullPipeline: (doc: MedicalDocument) => void;
  onRerunOcrOnly: (doc: MedicalDocument) => void;
  onRegenerateSummary: (docId: number) => void;
  onRerunClassification: (docId: number) => void;
  onDelete: (docId: number) => void;
}

const MemberDocumentCard: React.FC<MemberDocumentCardProps> = ({
  doc,
  isAdvanced,
  classifiers,
  docActiveActions,
  onRerunFullPipeline,
  onRerunOcrOnly,
  onRegenerateSummary,
  onRerunClassification,
  onDelete,
}) => {
  const anyBusy = docActiveActions[doc.id] != null || isDocumentPipelineBusy(doc);
  const ocrDone = isDocOcrDone(doc);
  const tone = cardTone(doc, anyBusy, ocrDone);
  const fileType = displayFileType(doc.file_type, doc.original_filename).toLowerCase();
  const fileCount = doc.file_count ?? 1;
  const isGrouped = fileCount > 1;

  const previewBadgeFor = (
    key: string
  ): { variant: string; format: (v: string) => string } => {
    if (key === 'search_tag') return { variant: 'badge-amber', format: formatClassificationTag };
    const variant =
      key === 'document_type'
        ? 'badge-teal'
        : key === 'specialty'
          ? 'badge-blue'
          : key === 'imaging_modality'
            ? 'badge-amber'
            : key === 'imaging_body_region'
              ? 'badge-slate'
              : 'badge-slate';
    return { variant, format: (v: string) => formatClassifierValue(classifiers, key, v) };
  };

  const renderClassificationPreview = () => {
    const skip = (s: string) => !s || s.toLowerCase() === 'none' || s.toLowerCase() === 'unknown';
    const f = doc.facets ?? {};
    const cardClassifiers = classifiers.filter((c) => c.show_in_card);
    const docTypesNormSet = new Set(
      (doc.document_types ?? []).map((v) => v.trim().toLocaleLowerCase('ro-RO')),
    );

    const segments = cardClassifiers
      .map((c) => {
        let values = (f[c.key] ?? []).filter((v) => !skip(v));
        // The `document_type` classifier values are already rendered
        // as the dedicated meta-row pills above; skip the duplicate
        // chip row here when every facet value is a match.
        if (c.key === 'document_type' && values.length > 0) {
          const everyMatch = values.every((v) =>
            docTypesNormSet.has(v.trim().toLocaleLowerCase('ro-RO')),
          );
          if (everyMatch) values = [];
        }
        return { classifier: c, values };
      })
      .filter((s) => s.values.length > 0);

    const clsStatus = doc.classification_status;
    const pipe = doc.pipeline_status || doc.ocr_processing_status || '';
    const ocrBusy =
      pipe &&
      pipe !== 'completed' &&
      !['summary_completed', 'classification_completed'].includes(pipe);

    let statusLine: string | null = null;
    if (clsStatus === 'generating') statusLine = 'Clasificare AI în curs…';
    else if (clsStatus === 'error') statusLine = 'Clasificare indisponibilă';
    else if (segments.length === 0 && ocrBusy) statusLine = formatOcrStatusShort(pipe);

    if (segments.length === 0 && !statusLine) return null;

    return (
      <div className="doc-card-tags">
        {segments.length > 0 && (
          <div className="doc-card-tags-row">
            {segments.map(({ classifier, values }) => {
              const { variant, format } = previewBadgeFor(classifier.key);
              const isTagLike = classifier.key === 'search_tag' || values.length > 6;
              const visible = isAdvanced || !isTagLike ? values : values.slice(0, 3);
              const extra = values.length - visible.length;
              return (
                <React.Fragment key={`pv-${classifier.key}`}>
                  {visible.map((v) => (
                    <span
                      key={`pv-${classifier.key}-${v}`}
                      className={`badge ${variant} doc-card-tag`}
                      title={`${classifier.label_ro}: ${format(v)}`}
                    >
                      {format(v)}
                    </span>
                  ))}
                  {extra > 0 && (
                    <span
                      className={`badge ${variant} doc-card-tag doc-card-tag-more`}
                      title={values.slice(visible.length).join(', ')}
                    >
                      +{extra}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
        {statusLine && <div className="doc-card-tags-status">{statusLine}</div>}
      </div>
    );
  };

  // Compact status pill in the header. Only rendered when something
  // worth surfacing is going on — completed/idle cards stay quiet so
  // the eye lands on the title + tags instead.
  const renderStatusPill = () => {
    const stage = memberDocPipelineStage(doc);
    if (!stage || stage === 'completed') return null;
    if (tone === 'idle') return null;

    const label = isAdvanced
      ? statusLabel[stage] || formatOcrStatusShort(stage) || stage
      : anyBusy
        ? 'Se procesează…'
        : tone === 'error'
          ? 'Eroare'
          : 'În așteptare';

    return (
      <span
        className={`doc-card-status doc-card-status--${tone}`}
        title={statusLabel[stage] || stage}
      >
        <span className="doc-card-status-dot" aria-hidden />
        {label}
      </span>
    );
  };

  const formattedDate = doc.document_date
    ? new Date(doc.document_date).toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : null;

  // The doc-level `document_types` array is the classifier's output
  // mirrored onto the row (so list responses don't have to load
  // facets just to render type pills). `renderClassificationPreview`
  // suppresses the duplicate `document_type` facet chip row when its
  // values match this list.
  const docTypes = doc.document_types ?? [];
  const trimmedNotes = (doc.notes ?? '').trim();

  return (
    <div
      className={`card admin-card doc-card-v2 doc-card-v2--${tone}`}
      data-grouped={isGrouped ? 'true' : 'false'}
    >
      <div className="doc-card-accent" aria-hidden />

      <div className="doc-card-head">
        <div
          className={`doc-card-fileicon ${fileIconVariant(fileType)} ${isGrouped ? 'is-stacked' : ''}`}
          aria-hidden
        >
          {fileIcon(fileType)}
          {isGrouped && <span className="doc-card-fileicon-stack" />}
        </div>
        <div className="doc-card-titleblock">
          <Link
            to={`/documents/${doc.id}`}
            className="doc-card-title"
            title={doc.original_filename}
          >
            {doc.original_filename}
          </Link>
          <div className="doc-card-titlemeta">
            <span className="doc-card-typepill">{fileType.toUpperCase()}</span>
            {isGrouped && (
              <span
                className="doc-card-grouped"
                title="Document grupat — conține mai multe fișiere"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                {fileCount} fișiere
              </span>
            )}
          </div>
        </div>
        {renderStatusPill()}
      </div>

      {(formattedDate || docTypes.length > 0) && (
        <div className="doc-card-meta">
          {formattedDate && (
            <span className="doc-card-meta-item" title="Data documentului">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {formattedDate}
            </span>
          )}
          {docTypes.length > 0 && (
            <span className="doc-card-meta-item doc-card-meta-types" title="Tip document">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M9 11H7v6h2v-6Zm4-3h-2v9h2V8Zm4-3h-2v12h2V5Z" />
                <rect x="3" y="3" width="18" height="18" rx="2" />
              </svg>
              {docTypes.map((t) => (
                <span
                  key={`type-${t}`}
                  className="badge badge-teal doc-card-tag"
                  title={`Tip document: ${formatClassifierValue(classifiers, 'document_type', t)}`}
                >
                  {formatClassifierValue(classifiers, 'document_type', t)}
                </span>
              ))}
            </span>
          )}
        </div>
      )}

      {trimmedNotes && (
        <div
          className="doc-card-description"
          title={trimmedNotes}
          aria-label="Descriere document"
        >
          {trimmedNotes}
        </div>
      )}

      {renderClassificationPreview()}

      <div className="doc-card-actions">
        <Link to={`/documents/${doc.id}`} className="btn btn-primary btn-sm flex-1">
          Deschide
        </Link>
        <div className="doc-card-actions-icons" role="group" aria-label="Acțiuni document">
          <button
            type="button"
            className="btn btn-secondary btn-sm btn-icon"
            onClick={() => onRerunFullPipeline(doc)}
            disabled={anyBusy}
            title="Reprocesare completă: OCR, rezumat AI și clasificare"
            aria-label="Reprocesare completă"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="m12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
              <path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65" />
              <path d="m22 12.65-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
            </svg>
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm btn-icon"
            onClick={() => onRerunOcrOnly(doc)}
            disabled={anyBusy}
            title="Reprocesare OCR (fără rezumat/clasificare)"
            aria-label="Reprocesare OCR"
          >
            {isDocOcrRunning(doc) ? (
              <span className="spinner" style={{ width: 14, height: 14 }} />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="23 4 23 10 17 10" />
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm btn-icon"
            onClick={() => onRegenerateSummary(doc.id)}
            disabled={anyBusy || !ocrDone}
            title="Regenerează rezumatul AI (fără reprocesare OCR)"
            aria-label="Regenerează rezumat"
          >
            {isDocSummaryGenerating(doc) ? (
              <span className="spinner" style={{ width: 14, height: 14 }} />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="8" y1="13" x2="16" y2="13" />
                <line x1="8" y1="17" x2="16" y2="17" />
                <line x1="8" y1="9" x2="12" y2="9" />
              </svg>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm btn-icon"
            onClick={() => onRerunClassification(doc.id)}
            disabled={anyBusy || !ocrDone}
            title="Reclasificare AI (necesită OCR finalizat)"
            aria-label="Reclasificare AI"
          >
            {isDocClassifying(doc) ? (
              <span className="spinner" style={{ width: 14, height: 14 }} />
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2Z" />
                <path d="M7 7h.01" />
              </svg>
            )}
          </button>
        </div>
        <button
          type="button"
          className="btn btn-danger btn-sm btn-icon"
          onClick={() => onDelete(doc.id)}
          title="Șterge"
          aria-label="Șterge document"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default MemberDocumentCard;
